import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  { icon: Code, title: "B2B portals and secure client dashboards" },
  { icon: Shield, title: "Business automation systems" },
  { icon: Layers, title: "Custom CRM and internal management tools" },
  { icon: TrendingUp, title: "Financial and calculation-based platforms" },
  { icon: Zap, title: "Workflow and operations software" },
  { icon: Smartphone, title: "Payment gateway integrated web applications" },
];


const processSteps = [
  {
    number: 1,
    title: "Discovery & Requirement Analysis",
    description:
      "We define project goals, business workflows, and technical requirements.",
  },
  {
    number: 2,
    title: "Architecture & Planning",
    description: "System structure, database planning, and scalability mapping.",
  },
  {
    number: 3,
    title: "Design & Development",
    description:
      "Front-end UI/UX combined with secure backend development.",
  },
  {
    number: 4,
    title: "Testing & Optimization",
    description:
      "Performance testing, security validation, and functionality checks.",
  },
  {
    number: 5,
    title: "Launch & Ongoing Support",
    description:
      "Deployment with monitoring and continuous improvement options.",
  },
];
const faqs = [
  {
    question: "How long does it take to build a custom web application?",
    answer:
      "Project timelines vary based on complexity. Most custom web applications take several weeks to a few months depending on features and integrations.",
  },
  {
    question: "How much does custom web application development cost in Vancouver?",
    answer:
      "Costs depend on functionality, integrations, and scalability requirements. We provide tailored quotes after understanding your project scope.",
  },
  {
    question: "What is the difference between custom and off-the-shelf software?",
    answer:
      "Custom software is built specifically for your business processes, while off-the-shelf software serves general use cases and may require compromises.",
  },
  {
    question: "Can you integrate payment gateways?",
    answer:
      "Yes. We integrate secure payment gateways, APIs, and third-party systems as required.",
  },
  {
    question: "Do you provide ongoing support?",
    answer:
      "Yes. We offer maintenance, upgrades, and optimization services after launch.",
  },
];
const whyChooseUs = [
  { title: "100% Custom Architecture — No templates or generic builds" },
  { title: "SEO-Friendly Development Structure" },
  { title: "Secure & Scalable Backend Systems" },
  { title: "Ongoing Support & Optimization" },
];

export default function WebsiteDevelopmentPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      {/* SEO Meta */}
<SEO
  title="Custom Web Application Development Vancouver | Square Trigger"
  description="Looking for custom web application development in Vancouver? Square Trigger builds secure, scalable web applications tailored for businesses across Canada."
  ogTitle="Custom Web Application Development Vancouver | Square Trigger"
  ogDescription="Looking for custom web application development in Vancouver? Square Trigger builds secure, scalable web applications tailored for businesses across Canada."
/>


      {/* Hero Section */}
<section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-18 lg:pb-20 relative overflow-hidden">
  <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
  <div className="absolute inset-0">
    <div className="absolute top-16 sm:top-20 right-6 sm:right-20 w-72 sm:w-96 h-72 sm:h-96 bg-primary/20 rounded-full blur-3xl" />
    <div className="absolute bottom-16 sm:bottom-20 left-6 sm:left-20 w-56 sm:w-72 h-56 sm:h-72 bg-secondary/20 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <div className="max-w-4xl text-center sm:text-left">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* (empty on purpose) */}
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-5 sm:mb-6 leading-tight"
      >
        Custom Web Application Development in{" "}
        <span className="gradient-text">Vancouver</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-lg sm:text-xl text-background/70 mb-8 max-w-2xl leading-relaxed mx-auto sm:mx-0"
      >
        Square Trigger builds secure, scalable custom web applications tailored to your
        business workflows, automation needs, and long-term growth goals.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center sm:items-start"
      >
        <Button
          variant="gradient"
          size="lg"
          asChild
          className="group w-full sm:w-auto justify-center"
        >
          <Link to="/get-started" className="flex items-center justify-center gap-2">
            Start Your Project
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </motion.div>
    </div>
  </div>
</section>

     {/* Value Statement */}
<section className="pt-12 sm:pt-14 lg:pt-20 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
    <div className="absolute -bottom-24 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="max-w-5xl mx-auto text-center"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
        Custom Web Applications <span className="gradient-text">Built for Real Business Operations</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-4">
        At <strong>Square Trigger</strong>, we design and develop custom web applications that
        solve operational challenges, streamline processes, and improve
        efficiency. Unlike off-the-shelf software, our solutions are built
        specifically for your business model.
      </p>

      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
        We work with startups, B2B companies, and growing organizations across
        Vancouver and Canada to develop scalable web-based platforms that
        support automation, integrations, and performance.
      </p>
    </motion.div>
  </div>
</section>

      {/* Why Choose Us */}
<section className="pt-12 sm:pt-14 lg:pt-14 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-10"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        Why Businesses Choose <span className="gradient-text">Square Trigger</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
        At Square Trigger, our approach combines technical expertise with business strategy
      </p>
    </motion.div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {whyChooseUs.map((item, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.08 }}
          className="p-6 bg-card rounded-2xl border border-border shadow-soft hover:shadow-medium transition-shadow text-left"
        >
          <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center mb-4">
            <div className="w-2.5 h-2.5 rounded-full bg-primary" />
          </div>

          <h3 className="font-semibold text-foreground">
            {item.title}
          </h3>
        </motion.div>
      ))}
    </div>

    <div className="mt-10 text-center max-w-3xl mx-auto">
      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
        We don’t just write code - we build digital infrastructure that supports measurable growth.
      </p>
    </div>
  </div>
</section>

     {/* Capabilities */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-12 sm:mb-14"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        Custom Web Applications <span className="gradient-text">We Develop</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
        We specialize in building:
      </p>
    </motion.div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {capabilities.map((capability, index) => {
        const Icon = capability.icon;
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.07 }}
            className="h-full"
          >
            <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover">
              <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                <Icon className="h-6 w-6 text-primary-foreground" />
              </div>

              <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground">
                {capability.title}
              </h3>
            </Card>
          </motion.div>
        );
      })}
    </div>

    <div className="mt-10 text-center max-w-3xl mx-auto">
      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
        Every application is developed with performance, scalability, and security in mind.
      </p>
    </div>
  </div>
</section>

{/* Process */}
<section className="pt-12 sm:pt-14 lg:pt-12 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        Our Custom Web Application <span className="gradient-text">Development Process</span>
      </h2>
    </motion.div>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {processSteps.map((step, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.08 }}
          className="relative h-full"
        >
          <Card className="p-6 h-full border-border hover:border-primary/30 transition-colors card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="text-3xl font-display font-bold gradient-text">
                {step.number}
              </div>
              <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center">
                <div className="w-2.5 h-2.5 rounded-full bg-primary" />
              </div>
            </div>

            <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
              {step.title}
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              {step.description}
            </p>
          </Card>
        </motion.div>
      ))}
    </div>
  </div>
</section>

{/* Modern Tech + Security Standards */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute -top-24 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
    <div className="absolute -bottom-24 left-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
        Built with Modern Technologies &{" "}
        <span className="gradient-text">Security Standards</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
        We use industry-standard frameworks and secure coding practices to ensure:
      </p>
    </motion.div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {[
        { icon: Zap, title: "Fast loading speeds" },
        { icon: Code, title: "Clean, scalable code" },
        { icon: Shield, title: "Data encryption & secure authentication" },
        { icon: Layers, title: "Role-based access systems" },
        { icon: Globe, title: "API & third-party integrations" },
       
      ].map((item, index) => {
        const Icon = item.icon;
        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.07 }}
            className="h-full"
          >
            <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover">
              <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                <Icon className="h-6 w-6 text-primary-foreground" />
              </div>

              <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground">
                {item.title}
              </h3>
            </Card>
          </motion.div>
        );
      })}
    </div>

    <div className="mt-10 text-center max-w-3xl mx-auto">
      <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
        Your application is built to scale as your business grows
      </p>
    </div>
  </div>
</section>


      

      {/* FAQs */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
        Custom Web Application Development –{" "}
        <span className="gradient-text">FAQs</span>
      </h2>
    </motion.div>

    <div className="max-w-3xl mx-auto space-y-4">
      {faqs.map((faq, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.05 }}
          className="bg-card rounded-xl border border-border overflow-hidden"
        >
          <button
            onClick={() => setOpenFaq(openFaq === index ? null : index)}
            className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-accent/50 transition-colors"
          >
            <span className="font-semibold text-foreground pr-6">
              {faq.question}
            </span>
            <ChevronDown
              className={`h-5 w-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${
                openFaq === index ? "rotate-180" : ""
              }`}
            />
          </button>

          <AnimatePresence>
            {openFaq === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground leading-relaxed">
                  {faq.answer}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>

    {/* FAQ Schema */}
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      })}
    </script>
  </div>
</section>

     {/* Final CTA */}
<section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto"
    >
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4 sm:mb-6">
        Build a Custom Web Application That Supports{" "}
        <span className="gradient-text">Growth</span>
      </h2>

      <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
        Partner with Square Trigger to develop a secure, scalable web application
        tailored to your business needs.
      </p>

      <div className="flex justify-center">
        <Button
          variant="gradient"
          size="lg"
          asChild
          className="group w-full sm:w-auto justify-center"
        >
          <Link to="/get-started" className="flex items-center justify-center gap-2">
            Request a Quote
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </div>
    </motion.div>
  </div>
</section>
    </Layout>
  );
}